import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import searchService from '../services/SearchService';

// Improved debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    return timeout; // Return timeout ID for potential cancellation
  };
};

// Main search hook with advanced features
export const useAdvancedSearch = (initialQuery = '', options = {}) => {
  // State management
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [isTyping, setIsTyping] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [initialized, setInitialized] = useState(false);

  // Refs
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Generate search suggestions based on query
  const generateSuggestions = useCallback((searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 1) {
      setSearchSuggestions([]);
      return;
    }

    const suggestions = searchService.generateSearchSuggestions(searchQuery);
    setSearchSuggestions(suggestions);
  }, []);

  // Debounced search function with improved error handling - Optimized for instant feel
  const debouncedSearch = useMemo(
    () => debounce(async (searchQuery, searchOptions = {}) => {

      if (!searchQuery || searchQuery.trim().length < 2) {

        setResults([]);
        setHasMore(false);
        setLoading(false);
        setIsTyping(false);
        return;
      }

      // Cancel previous request
      if (abortControllerRef.current) {

        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);
      setIsTyping(false);

      try {
        const result = await searchService.searchProducts(searchQuery, {
          ...options,
          ...searchOptions,
          page: searchOptions.page || 1,
          signal: abortControllerRef.current.signal,
        });

        // Check if request was aborted
        if (abortControllerRef.current.signal.aborted) {
          return;
        }

        if (searchOptions.page === 1 || !searchOptions.page) {
          setResults(result.products);
        } else {
          setResults(prev => [...prev, ...result.products]);
        }

        setHasMore(result.hasMore);
        setFromCache(result.fromCache);
        setPage(result.page);
        setTotalResults(result.totalResults || 0);
        setInitialized(true);
      } catch (err) {
        if (err.name === 'AbortError') {

          return;
        }
        
        console.error('[useAdvancedSearch] Search error:', err);
        setError(err.message);
        setResults([]);
        setHasMore(false);
        setInitialized(true);
      } finally {
        setLoading(false);
      }
    }, 100), // Optimized to 100ms for instant feel
    [options]
  );

  // Handle query changes with improved initialization handling
  const handleQueryChange = useCallback((newQuery) => {

    setQuery(newQuery);
    generateSuggestions(newQuery);
    setIsTyping(true);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (newQuery.trim().length >= 2) {
      // Trigger search immediately without delay
      debouncedSearch(newQuery, { page: 1 });
    } else {
      setResults([]);
      setHasMore(false);
      setLoading(false);
      setIsTyping(false);
      setInitialized(true);
    }
  }, [debouncedSearch, generateSuggestions, query, initialized]);

  // Load more results
  const loadMore = useCallback(() => {
    if (hasMore && !loading && query.trim().length >= 2) {
      debouncedSearch(query, { page: page + 1 });
    }
  }, [hasMore, loading, query, page, debouncedSearch]);

  // Clear search
  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setSuggestions([]);
    setSearchSuggestions([]);
    setError(null);
    setHasMore(false);
    setPage(1);
    setIsTyping(false);
    setFromCache(false);
    setTotalResults(0);
    setInitialized(false); // Reset initialization state

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Refresh search results
  const refreshSearch = useCallback(() => {
    if (query.trim().length >= 2) {
      // Clear cache for this query
      searchService.clearCache();
      debouncedSearch(query, { page: 1 });
    }
  }, [query, debouncedSearch]);

  // Get search history
  const getSearchHistory = useCallback(() => {
    return searchService.getSearchHistory();
  }, []);

  // Clear search history
  const clearSearchHistory = useCallback(() => {
    searchService.clearSearchHistory();
  }, []);

  // Initial search effect - trigger search if initialQuery is provided
  useEffect(() => {
    if (initialQuery && initialQuery.trim().length >= 2 && !initialized) {

      debouncedSearch(initialQuery, { page: 1 });
    }
  }, []); // Only run once on mount

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // State
    query,
    results,
    suggestions,
    searchSuggestions,
    loading,
    error,
    hasMore,
    page,
    isTyping,
    fromCache,
    totalResults,
    initialized, // Expose initialized state

    // Actions
    handleQueryChange,
    loadMore,
    clearSearch,
    refreshSearch,
    getSearchHistory,
    clearSearchHistory,
    generateSuggestions,

    // Utilities
    setQuery,
    setResults,
    setError,
  };
};

// Hook for suggested products
export const useSuggestedProducts = () => {
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);

  const loadSuggestions = useCallback(async (pageNum = 1, append = false) => {
    // Use separate loading flag for pagination to avoid UI flash
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await searchService.getSuggestedProducts(pageNum);
      
      if (append) {
        setSuggestedProducts(prev => [...prev, ...result.products]);
      } else {
        setSuggestedProducts(result.products);
      }
      
      setHasMore(result.hasMore);
      setPage(pageNum);
    } catch (err) {
      setError(err.message);
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  const loadMore = useCallback(() => {
    if (hasMore && !loading) {
  loadSuggestions(page + 1, true);
    }
  }, [hasMore, loading, page, loadSuggestions]);

  const refresh = useCallback(() => {
    loadSuggestions(1, false);
  }, [loadSuggestions]);

  // Load initial suggestions
  useEffect(() => {
    loadSuggestions();
  }, []);

  return {
    suggestedProducts,
    loading,
  loadingMore,
    error,
    hasMore,
    page,
    loadMore,
    refresh,
  };
};

// Hook for search analytics (optional)
export const useSearchAnalytics = () => {
  const trackSearch = useCallback((query, resultsCount = 0) => {
    // Track search query for analytics
    try {
      // You can integrate with analytics services here
    } catch (error) {
      console.error('Failed to track search:', error);
    }
  }, []);

  const trackSearchClick = useCallback((query, productId, position) => {
    // Track search result clicks
    try {
      // Placeholder for analytics integration
    } catch (error) {
      console.error('Failed to track search click:', error);
    }
  }, []);

  return {
    trackSearch,
    trackSearchClick,
  };
};
